/**
 * AI Readiness Assessment — Interactive Questionnaire
 *
 * Two-stage flow:
 * 1. /tools/readiness-assessment             → AssessmentStartScreen
 *    (pick "Take it anonymously" or sign in to save progress).
 * 2. /tools/readiness-assessment?mode=assessment → Questionnaire
 *    (the actual 32-question, 7-category, 2-layer assessment).
 *
 * `?mode=assessment` is the signal that the user has chosen a path. The
 * Questionnaire component is self-contained: it loads the active version,
 * persists responses to localStorage + the backend, and calculates the
 * weighted score on submit. See modules/questionnaire/CLAUDE.md for the
 * full module contract.
 */

import { ArrowLeft } from "lucide-react";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PageSEO } from "@/components/SEO/PageSEO";
import { AssessmentStartScreen, Questionnaire } from "@/modules/questionnaire";
import { useAuthStore } from "@/stores/auth.store";

const ReadinessAssessment: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = useAuthStore((s) => s.profile);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const isInAssessment = searchParams.get("mode") === "assessment";

  // Authenticated users skip the chooser — they always save progress.
  const shouldShowQuestionnaire = isInAssessment || isAuthenticated;

  const startAnonymous = () => {
    setSearchParams({ mode: "assessment" });
  };

  return (
    <>
      <PageSEO
        title="AI Readiness Assessment"
        description="Evaluate your organization's readiness for AI implementation with our comprehensive 32-question assessment. Get a detailed scorecard with recommendations for successful AI automation adoption."
        keywords="AI readiness assessment, organizational readiness, AI implementation planning, automation readiness, AI adoption scorecard, business AI evaluation"
        ogImage="/og-image-tools.png"
      />

      <div className="min-h-screen bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="mb-8">
            <button
              onClick={() => navigate("/tools")}
              className="flex items-center gap-2 text-slate-600 hover:text-[#B87333] mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-semibold">Back to Tools</span>
            </button>

            <div className="text-center mb-2">
              <p className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-4">
                Organizational Assessment
              </p>
              <h1 className="text-3xl sm:text-5xl font-black text-slate-900 leading-tight mb-6">
                AI Readiness Assessment
              </h1>
              <p className="text-xl text-slate-600 max-w-[800px] mx-auto">
                Evaluate your organization's readiness for AI implementation
                across 7 categories with 32 targeted questions.
              </p>
            </div>
          </div>

          {shouldShowQuestionnaire ? (
            <Questionnaire userId={profile?.id} />
          ) : (
            <AssessmentStartScreen onStartAnonymous={startAnonymous} />
          )}
        </div>
      </div>
    </>
  );
};

export default ReadinessAssessment;
